/**
 * Labstep
 *
 * @module components/Chemical/Form/ShowEdit/Inventory/NoResources
 * @desc ShowEditInventoryNoResources for Chemical
 */

import { ResourceFormCreate } from 'labstep-web/components/Resource/Form/Create';
import { fieldResourceName } from 'labstep-web/components/Resource/Form/fields';
import { EntityCreateContainer } from 'labstep-web/containers/Entity/Create';
import type { EntityCreateContainerChildrenProps } from 'labstep-web/containers/Entity/Create/types';
import type { APIResponse } from 'labstep-web/models';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataParent } from 'labstep-web/models/metadata/types';
import { getSvgFromSmiles } from 'labstep-web/services/ketcher/utils';
import { enhanceOptions } from 'labstep-web/services/utils.service';
import type React from 'react';
import type {
  ChemicalFormShowEditInventoryNoResourcesContainerProps,
  ChemicalFormShowEditInventoryNoResourcesProps,
} from './types';

export const ChemicalFormShowEditInventoryNoResources: React.FC<
  ChemicalFormShowEditInventoryNoResourcesProps
> = ({ chemical, update, create, options }) => (
  <div>
    <p>
      No resources with this chemical structure were found in your
      inventory. Complete the form below to create a new one.
    </p>
    <ResourceFormCreate
      options={enhanceOptions({
        options,
        onSuccess: async ({
          response,
        }: {
          response?: APIResponse;
        }) => {
          if (!response) {
            return;
          }

          let svg = '';
          try {
            // Use Ketcher to generate SVG from SMILES instead of MarvinJS
            svg = await getSvgFromSmiles(
              chemical.properties.SMILES as string,
            );
          } catch (error) {
            console.error(
              'Error generating SVG with Ketcher:',
              error,
            );
            // SVG generation failed, but we can still create the metadata without it
            svg = '';
          }

          create({
            metadata_thread_id: (
              response.entities.resource[
                response.result as string | number
              ] as MetadataParent
            ).metadata_thread,
            type: 'molecule',
            label: 'Structure',
            molecule: {
              name: chemical.properties.IUPACName || 'Untitled',
              pubchem: {
                reactants: [chemical.properties],
                products: [],
              },
              data: chemical.properties.SMILES,
              svg: svg,
              smiles: chemical.properties.SMILES,
            },
          });
          update({ resource_id: response.result as string | number });
        },
      })}
      defaultValues={{
        [fieldResourceName.name]: chemical.properties.IUPACName,
      }}
    />
  </div>
);

export const ChemicalFormShowEditInventoryNoResourcesContainer: React.FC<
  ChemicalFormShowEditInventoryNoResourcesContainerProps
> = (props) => (
  <EntityCreateContainer entityName={Metadata.entityName}>
    {(
      containerProps: EntityCreateContainerChildrenProps<Metadata>,
    ) => (
      <ChemicalFormShowEditInventoryNoResources
        {...containerProps}
        {...props}
      />
    )}
  </EntityCreateContainer>
);
